import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const VACIA = {
  nombre: "",
  codigo: "",
  descripcion: "",
  facturable_default: true,
  tarifa_venta_hora: "",
  tarifa_coste_hora: "",
  orden: "0",
};

function numONull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function intOCero(v) {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
}

function payloadTarea(form) {
  return {
    nombre: (form.nombre || "").trim(),
    codigo: (form.codigo || "").trim(),
    descripcion: form.descripcion || "",
    facturable_default: form.facturable_default !== false,
    tarifa_venta_hora: numONull(form.tarifa_venta_hora),
    tarifa_coste_hora: numONull(form.tarifa_coste_hora),
    orden: intOCero(form.orden),
    activo: form.activo !== false,
  };
}

export default function ProyectoTareasDialog({ proyecto, open, onOpenChange, onChange }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const cargar = useCallback(async () => {
    if (!proyecto) return;
    try {
      const { data } = await api.get(`/proyectos/${proyecto.id}/tareas`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [proyecto]);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  const guardar = async () => {
    if (!form?.nombre?.trim()) return toast.error("Indica el nombre de la tarea");
    try {
      const body = payloadTarea(form);
      if (editId) await api.put(`/proyectos/tareas/${editId}`, body);
      else await api.post(`/proyectos/${proyecto.id}/tareas`, body);
      toast.success(editId ? "Tarea actualizada" : "Tarea creada");
      setForm(null);
      setEditId(null);
      cargar();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const eliminar = async (t) => {
    if (!window.confirm(`¿Eliminar «${t.nombre}»?`)) return;
    try {
      await api.delete(`/proyectos/tareas/${t.id}`);
      toast.success("Tarea eliminada");
      cargar();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tareas — {proyecto?.nombre}</DialogTitle>
        </DialogHeader>
        <Button
          className="w-fit"
          onClick={() => {
            setEditId(null);
            setForm({ ...VACIA });
          }}
        >
          <Plus className="mr-1 h-4 w-4" /> Nueva tarea
        </Button>
        <div className="space-y-2">
          {items.length ? (
            items.map((t) => (
              <div key={t.id} className="flex justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium">
                    {t.codigo ? `${t.codigo} · ` : ""}
                    {t.nombre}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {t.facturable_default !== false ? "Facturable" : "No facturable"}
                    {t.descripcion ? ` · ${t.descripcion}` : ""}
                  </p>
                </div>
                <div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditId(t.id);
                      setForm({
                        ...VACIA,
                        ...t,
                        tarifa_venta_hora: t.tarifa_venta_hora ?? "",
                        tarifa_coste_hora: t.tarifa_coste_hora ?? "",
                        orden: t.orden ?? 0,
                      });
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => eliminar(t)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="py-6 text-center text-sm text-zinc-500">No hay tareas.</p>
          )}
        </div>
        {form && (
          <div className="space-y-3 rounded-xl border bg-zinc-50 p-4">
            <p className="font-medium">{editId ? "Editar tarea" : "Nueva tarea"}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div>
                <Label>Código</Label>
                <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Descripción</Label>
              <Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Venta €/h</Label>
                <Input
                  type="number"
                  value={form.tarifa_venta_hora}
                  onChange={(e) => setForm({ ...form, tarifa_venta_hora: e.target.value })}
                />
              </div>
              <div>
                <Label>Coste €/h</Label>
                <Input
                  type="number"
                  value={form.tarifa_coste_hora}
                  onChange={(e) => setForm({ ...form, tarifa_coste_hora: e.target.value })}
                />
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={form.orden}
                  onChange={(e) => setForm({ ...form, orden: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.facturable_default !== false}
                onCheckedChange={(v) => setForm({ ...form, facturable_default: v })}
              />
              <Label>Facturable por defecto</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setForm(null)}>
                Cancelar
              </Button>
              <Button onClick={guardar}>Guardar tarea</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
